import {
  EInvoiceAddressSchemeType,
  EInvoicingAddress,
  EntityType,
  getDefaultAddressSchemeTypeByCountry,
} from '@components'

import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'

import { useEInvoicingAddressForm } from '../../../../../../contexts/eInvoicingAddressFormContext'
import { getAddressingLineLookupErrorMessage } from '../../../../../../utils/getAddressingLineLookupErrorMessage'
import { AddressingLineInputLabel } from './elements/AddressingLineInputLabel'

export const CustomerEInvoicingAddress = (): ReactElement => {
  const { t } = useTranslation()
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()
  const { eInvoicingAddressForm, lookupStatus, setIsUnregisteredAddressingLineAcknowledged } =
    useEInvoicingAddressForm()
  const {
    formState: { errors },
    setValue,
    trigger,
    watch,
  } = eInvoicingAddressForm

  const [addressingLine, customerAddress, customerEInvoiceAddress, entityType] = watch([
    'addressingLine',
    'address',
    'eInvoiceAddress',
    'entityType',
  ])
  const { country } = customerAddress || {}
  const { address, scheme } = customerEInvoiceAddress || {}
  const eInvoiceAddressErrorMessage = errors?.eInvoiceAddress?.address?.message
  const addressingLineErrorMessage = errors?.addressingLine?.message
  // a failed lookup is reported on the field itself, the same way a validation error is
  const addressingLineFieldErrorMessage =
    addressingLineErrorMessage ?? getAddressingLineLookupErrorMessage(t, lookupStatus)

  const inputPlaceholder = useMemo(() => {
    return isFrenchComplianceMode && entityType === EntityType.Private
      ? t('external_invoices.send_as_e_invoice.modal.field.addressing_line.placeholder')
      : undefined
  }, [entityType, isFrenchComplianceMode, t])

  const handleAddressingLineInputChange = useCallback(
    (newAddressingLine?: string) => {
      setValue('addressingLine', newAddressingLine)
      setIsUnregisteredAddressingLineAcknowledged(false)

      if (addressingLineErrorMessage) {
        trigger('addressingLine')
      }
    },
    [addressingLineErrorMessage, setIsUnregisteredAddressingLineAcknowledged, setValue, trigger],
  )

  const handleEInvoiceAddressInputChange = useCallback(
    (newAddress?: string) => {
      setValue('eInvoiceAddress.address', newAddress)

      if (eInvoiceAddressErrorMessage) {
        trigger('eInvoiceAddress.address')
      }
    },
    [eInvoiceAddressErrorMessage, setValue, trigger],
  )

  const handleEInvoiceAddressSchemeTypeChange = useCallback(
    (newSchemeType?: EInvoiceAddressSchemeType) => {
      setValue('eInvoiceAddress.scheme', newSchemeType)
      handleEInvoiceAddressInputChange('')
    },
    [handleEInvoiceAddressInputChange, setValue],
  )

  const handleEInvoiceAddressEntityTypeChange = useCallback(
    (newEntityType?: EntityType) => {
      setValue('entityType', newEntityType)

      const isPublicEntity = newEntityType === EntityType.Public
      const defaultSchemeType = getDefaultAddressSchemeTypeByCountry(isPublicEntity, country)
      handleEInvoiceAddressSchemeTypeChange(defaultSchemeType)
    },
    [country, handleEInvoiceAddressSchemeTypeChange, setValue],
  )

  const handleAddressInputChange = useCallback(
    (addressingLine?: string) => {
      if (isFrenchComplianceMode) {
        handleAddressingLineInputChange(addressingLine)
        return
      }

      handleEInvoiceAddressInputChange(addressingLine)
    },
    [isFrenchComplianceMode, handleAddressingLineInputChange, handleEInvoiceAddressInputChange],
  )

  return (
    <EInvoicingAddress
      address={isFrenchComplianceMode ? addressingLine : address}
      countryId={country}
      eInvoiceAddressInputErrorMessage={
        isFrenchComplianceMode ? addressingLineFieldErrorMessage : eInvoiceAddressErrorMessage
      }
      entityType={entityType}
      inputLabel={isFrenchComplianceMode ? <AddressingLineInputLabel /> : undefined}
      inputPlaceholder={inputPlaceholder}
      onEInvoiceAddressEntityTypeChange={handleEInvoiceAddressEntityTypeChange}
      onEInvoiceAddressInputChange={handleAddressInputChange}
      onEInvoiceAddressSchemeTypeChange={handleEInvoiceAddressSchemeTypeChange}
      schemeType={scheme || ''}
      size="sm"
    />
  )
}
